import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import CommonAPIService from '../api/CommonAPIService';
import { getTourStatusList } from '../api/tourStatusService';
import { userAuth } from './userContext';

const TourStatusContext = createContext({
	tourStatusList: [],
	isTourStatusLoading: false,
	tourStatusError: null,
	refreshTourStatusList: async () => {},
	setTourModuleStatus: () => {},
	manualTourModule: null,
	triggerManualTour: () => {},
	clearManualTour: () => {},
});

export const useTourStatus = () => useContext(TourStatusContext);

export const TourStatusProvider = ({ children }) => {
	const APIService = CommonAPIService();
	const { token } = useContext(userAuth);
	const sendRequestRef = useRef(APIService.sendRequest);
	const lastSessionKeyRef = useRef('');
	const isMountedRef = useRef(true);

	const [tourStatusList, setTourStatusList] = useState([]);
	const [isTourStatusLoading, setIsTourStatusLoading] = useState(false);
	const [tourStatusError, setTourStatusError] = useState(null);
	const [manualTourModule, setManualTourModule] = useState(null);

	useEffect(() => {
		sendRequestRef.current = APIService.sendRequest;
	}, [APIService]);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const refreshTourStatusList = useCallback(async () => {
		if (isMountedRef.current) {
			setIsTourStatusLoading(true);
			setTourStatusError(null);
		}
		try {
			const list = await getTourStatusList(sendRequestRef.current);
			if (isMountedRef.current) {
				setTourStatusList(Array.isArray(list) ? list : []);
			}
			return Array.isArray(list) ? list : [];
		} catch (error) {
			console.error('Failed to load tour status list:', error);
			if (isMountedRef.current) {
				setTourStatusError(error);
			}
			return [];
		} finally {
			if (isMountedRef.current) {
				setIsTourStatusLoading(false);
			}
		}
	}, []);

	const setTourModuleStatus = useCallback((moduleId, status) => {
		if (!moduleId) return;

		setTourStatusList((currentList) =>
			currentList.map((item) =>
				Number(item?.id) === Number(moduleId)
					? { ...item, tour_guide_status: Number(status) }
					: item,
			),
		);
	}, []);

	const triggerManualTour = useCallback((moduleName) => {
		setManualTourModule(moduleName || null);
	}, []);

	const clearManualTour = useCallback(() => {
		setManualTourModule(null);
	}, []);

	useEffect(() => {
		const sessionKey = token?.session_key || '';
		if (!sessionKey) return;
		if (lastSessionKeyRef.current === sessionKey) return;

		lastSessionKeyRef.current = sessionKey;
		refreshTourStatusList();
	}, [refreshTourStatusList, token?.session_key]);

	const value = useMemo(
		() => ({
			tourStatusList,
			isTourStatusLoading,
			tourStatusError,
			refreshTourStatusList,
			setTourModuleStatus,
			manualTourModule,
			triggerManualTour,
			clearManualTour,
		}),
		[
			tourStatusList,
			isTourStatusLoading,
			tourStatusError,
			refreshTourStatusList,
			setTourModuleStatus,
			manualTourModule,
			triggerManualTour,
			clearManualTour,
		],
	);

	return <TourStatusContext.Provider value={value}>{children}</TourStatusContext.Provider>;
};

export default TourStatusContext;
